import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { TaggedList } from '@client/components/_ui/display/TaggedList';
import { Confirm } from '@client/components/_ui/feedback/Confirm';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createClientColumnHelper, PrismaTable, useClientTable } from '@client/hooks/table-v2';
import { type RouterOutputs, trpc } from '@client/trpc/client';
import { CONTACT_TYPE_TO_TAG } from '@shared/constants';
import { CONTACT_FIELD_TYPE_LABELS } from '@shared/constants/business/product';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { Archive, ArchiveRestore, Contact } from 'lucide-react';
import { useState } from 'react';

/**
 * Archive asks first (client conventions: archive is destructive) and hides the tooltip
 * while the confirmation is open, as `Button.Delete` does; restore is a plain action.
 */
function ArchiveRoleButton({
    role,
    onToggle,
}: {
    role: { name: string; isArchived: boolean };
    onToggle: (isArchived: boolean) => void;
}) {
    const [confirmOpen, setConfirmOpen] = useState(false);

    if (role.isArchived) {
        return (
            <Button.Icon
                size="small"
                icon={<ArchiveRestore />}
                tooltip="Restore"
                aria-label={`Restore ${role.name}`}
                onClick={(e) => {
                    e.stopPropagation();
                    onToggle(false);
                }}
            />
        );
    }

    return (
        <Confirm
            title={`Archive ${role.name}?`}
            description="New product versions can no longer pick this role. Versions already using it keep working, and you can restore it later."
            okText="Archive"
            okDanger
            onOk={() => onToggle(true)}
            onOpenChange={setConfirmOpen}
        >
            <Button.Icon
                size="small"
                icon={<Archive />}
                tooltip="Archive"
                tooltipDisabled={confirmOpen}
                aria-label={`Archive ${role.name}`}
                onClick={(e) => e.stopPropagation()}
            />
        </Confirm>
    );
}

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/contact-roles')({
    staticData: {
        metadata: {
            title: 'Contact roles',
        },
        ui: {
            icon: Contact,
            title: 'Contact roles',
            subtitle: 'The contact roles product versions are built from',
            useTitleForChildren: true,
            menuDescription: 'Reusable contact roles for product versions',
        },
        presentation: 'fullPage',
        modules: ['claim'],
        permissions: ['settings:update'],
        featureFlag: 'is-products-v2-enabled',
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ContactRoles />
        </PageController>
    );
}

type ContactRoleRow = RouterOutputs['orgContactRole']['listOrgContactRoles']['rows'][number];

const columnHelper = createClientColumnHelper<ContactRoleRow>();

type View = 'active' | 'archived';

function ContactRoles() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const [view, setView] = useState<View>('active');

    const { data, isPending, error, refetch, isFetching } = trpc.orgContactRole.listOrgContactRoles.useQuery({
        where: { isArchived: view === 'archived' },
    });
    const { mutate: setArchived } = trpc.orgContactRole.setOrgContactRoleArchived.useMutation({
        onSuccess() {
            void trpcUtils.orgContactRole.invalidate();
        },
        onError(error) {
            errorMessage.show(error);
        },
    });
    const { mutate: deleteRole } = trpc.orgContactRole.deleteOrgContactRole.useMutation();

    const tableProps = useClientTable({
        data: data?.rows ?? [],
        rowKey: 'id',
        loading: isPending,
        pagination: false,
        columns: [
            columnHelper.accessor('name', { id: 'name', header: 'Name', className: 'font-medium' }),
            columnHelper.accessor('systemType', {
                id: 'systemType',
                header: 'Built-in role',
                cell({ row }) {
                    return row.systemType ? (
                        <Tag>{CONTACT_TYPE_TO_TAG[row.systemType]}</Tag>
                    ) : (
                        <span className="text-subtle-foreground">None</span>
                    );
                },
            }),
            columnHelper.accessor('defaultFields', {
                id: 'defaultFields',
                header: 'Default fields',
                cell({ row }) {
                    return (
                        <TaggedList
                            items={row.defaultFields}
                            accessor={(item) => CONTACT_FIELD_TYPE_LABELS[item.type]}
                            emptyText="No template"
                        />
                    );
                },
            }),
            columnHelper.accessor('usageCount', {
                id: 'usageCount',
                header: 'Used by',
                align: 'right',
                cell({ row }) {
                    return row.usageCount ? (
                        `${row.usageCount} version${row.usageCount === 1 ? '' : 's'}`
                    ) : (
                        <span className="text-subtle-foreground">Unused</span>
                    );
                },
            }),
            columnHelper.accessor('isSeeded', {
                id: 'isSeeded',
                header: 'Source',
                cell({ row }) {
                    return row.isSeeded ? (
                        <Tag variant="outlined" color="default">
                            Seeded
                        </Tag>
                    ) : (
                        <Tag variant="outlined" color="purple">
                            Custom
                        </Tag>
                    );
                },
            }),
            columnHelper.accessor('isArchived', {
                id: 'isArchived',
                header: 'Status',
                cell({ row }) {
                    return <StatusBadge module="state" status={row.isArchived ? 'ARCHIVED' : 'ACTIVE'} />;
                },
            }),
            columnHelper.actions({
                cell({ row }) {
                    return (
                        // Row clicks open the editor; keep the actions (and their portalled
                        // confirms) from also triggering it.
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button.Edit.Link
                                to="/console/$orgId/configuration/claims-products/contact-roles/edit/$contactRoleId"
                                params={{ orgId, contactRoleId: row.id }}
                                aria-label={`Edit ${row.name}`}
                            />
                            <ArchiveRoleButton
                                role={row}
                                onToggle={(isArchived) => setArchived({ id: row.id, isArchived })}
                            />
                            <Button.Delete
                                disabled={row.usageCount > 0}
                                tooltip={
                                    row.usageCount > 0 ? 'In use by a product version — archive it instead' : undefined
                                }
                                onConfirm={(onSuccess) =>
                                    deleteRole(
                                        { id: row.id },
                                        {
                                            onSuccess() {
                                                onSuccess();
                                                void trpcUtils.orgContactRole.invalidate();
                                            },
                                            onError(error) {
                                                errorMessage.show(error);
                                            },
                                        },
                                    )
                                }
                            />
                        </div>
                    );
                },
            }),
        ],
    });

    // The editor drawer renders through the Outlet, so it stays mounted whatever the list
    // query is doing: a failed background refetch must not unmount a dirty form. The error
    // shows beside the rows the query still holds from its last success.
    const listError = error ? (
        <InlineQueryError error={error} onRetry={() => void refetch()} retrying={isFetching} />
    ) : null;

    return (
        <>
            <Outlet />
            {listError && !data ? (
                listError
            ) : (
                <>
                    {listError}
                    <TableToolbar
                        filters={
                            <SegmentedControl<View>
                                aria-label="Contact role status"
                                size="small"
                                value={view}
                                onChange={setView}
                                options={[
                                    { value: 'active', label: 'Active' },
                                    { value: 'archived', label: 'Archived' },
                                ]}
                            />
                        }
                        actions={
                            <Button.Add.Link
                                to="/console/$orgId/configuration/claims-products/contact-roles/add"
                                params={{ orgId }}
                                variant="primary"
                                label="New contact role"
                            />
                        }
                    />
                    <PrismaTable
                        {...tableProps}
                        onRow={({ id }) => ({
                            className: 'cursor-pointer',
                            onClick(e) {
                                navigate(
                                    {
                                        to: '/console/$orgId/configuration/claims-products/contact-roles/edit/$contactRoleId',
                                        params: { orgId, contactRoleId: id },
                                    },
                                    e,
                                );
                            },
                        })}
                    />
                </>
            )}
        </>
    );
}
